import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import api from '../api/api';

// Create the UserContext
export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const { isAuthenticated, user: authUser } = useContext(AuthContext);
  const [user, setUser] = useState(authUser || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user data when authentication state changes
  useEffect(() => {
    if (isAuthenticated && authUser) {
      setUser(authUser);
    } else {
      setUser(null);
    }
  }, [isAuthenticated, authUser]);

  // Function to refresh user data from the server
  const refreshUserData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/auth/me');
      const userData = response.data.user;
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Failed to refresh user data:', err);
      const errorMessage = err.response?.data?.message || 'Failed to refresh user data';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Function to update user data
  const updateUser = useCallback(async (userData) => {
    if (!isAuthenticated || !user?.id) {
      setError('You must be logged in to update your profile');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Send the updated data to the server
      const response = await api.put('/auth/profile', userData);
      
      // Extract user from response (handle nested data structure)
      const updatedUser = response.data?.data?.user || response.data?.user;
      
      if (updatedUser) {
        // Update the local state with the response from the server
        setUser(prevUser => ({ ...prevUser, ...updatedUser }));
        
        // Also update localStorage to persist changes
        const authContext = JSON.parse(localStorage.getItem('authUser') || '{}');
        const mergedUser = { ...authContext, ...updatedUser };
        localStorage.setItem('authUser', JSON.stringify(mergedUser));

        // Trigger a custom event to notify other components (like profile strength calculator)
        window.dispatchEvent(new CustomEvent('userProfileUpdated', { 
          detail: mergedUser 
        }));
      }
      
      return true;
    } catch (err) {
      console.error('Failed to update user data:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update user data';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  // Function to clear user errors
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <UserContext.Provider value={{ 
      user, 
      loading, 
      error, 
      refreshUserData, 
      updateUser,
      clearError
    }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
